/**
 * frontend/src/pages/valuation/_useMethodologyEdits.ts
 *
 * VAL-PATH-SIGNER P7b — the signer's edits on Methodology and rates: the rates
 * (P4b RatesSection, edit mode), the multiple override (moved off Intake), the
 * earnings override and the earnings basis (ADD 10-22 06:40). Each autosaves
 * through the shared draft (useAutosave); an override or a basis without its
 * reason is never saved. The earnings choices live in tuning extras, which the
 * server merges and stamps (by/at) — the preview's own tuning never carries them.
 *
 * Hydrates once, when the draft is in: nothing renders an editable control
 * before that (VAL-DRAFT-OVERWRITE-1), so a late draft is never saved over.
 *
 * FIX-SIGNER-PREFILL — then the server's starting values for this role fill
 * each rate the draft left empty and the signer has not typed in (the
 * FIX_VAL-OWNER-INPUTS-2 pattern on Intake). The server sends the signer only
 * the risk-free rate, projection years and the capitalized-earnings weight;
 * the judgment rates are never filled here.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { fetchValuationMethodology } from '@/api/valuation'
import { fetchNormalization } from '@/api/valuationNormalization'
import type { NormalizationOk } from '@/api/valuationNormalization'
import type { HouseDefault } from '@/types/valuation'
import type { DraftExtras, DraftInputs, SaveDraftBody } from '@/types/valuationDraft'
import type { RatesValues } from './_RatesUsed'
import { useAutosave } from './_useAutosave'
import { overrideAutosavePatch, ratesAutosavePatch, ratesFrom } from './_valuationIntakeHelpers'
import { BASIS_REASON_LABEL, EARNINGS_OVERRIDE_LABEL, EARNINGS_OVERRIDE_REASON_LABEL } from './_MethodologyEarnings'

export type BasisChoice = '' | 'sde' | 'adjusted_ebitda'

const AUTOSAVE_MS = 800
const notSaved = (fields: string[]) => (fields.length ? `Not saved — check ${fields.join(', ')}` : null)
const str = (v: unknown) => (v === null || v === undefined ? '' : String(v))
// The rates a server starting value may fill — never the judgment rates.
const STARTING_FIELDS: Array<['risk_free_rate' | 'projection_years' | 'capitalized_earnings_weight', keyof RatesValues]> = [
  ['risk_free_rate', 'riskFree'],
  ['projection_years', 'projectionYears'],
  ['capitalized_earnings_weight', 'capWeight'],
]

export function useMethodologyEdits(
  id: string | undefined,
  draft: DraftInputs | null,
  draftExtras: DraftExtras,
  saveDraft: (body: SaveDraftBody) => Promise<string | null>,
  canOverride: boolean,
) {
  const [normalized, setNormalized] = useState<NormalizationOk | null>(null)
  useEffect(() => {
    if (!id) return
    let live = true
    fetchNormalization(id)
      .then(v => { if (live && !('error' in v.result)) setNormalized(v.result) })
      .catch(() => {}) // the saved draft's figure stands in
    return () => { live = false }
  }, [id])

  const [hydrated, setHydrated] = useState(false)
  const [rates, setRates] = useState<RatesValues>(() => ratesFrom(null))
  const [overrideMultiple, setOverrideMultiple] = useState('')
  const [overrideRationale, setOverrideRationale] = useState('')
  const [overrideDisposition, setOverrideDisposition] = useState('')
  const [earningsOverride, setEarningsOverride] = useState('')
  const [earningsOverrideReason, setEarningsOverrideReason] = useState('')
  const [basisChoice, setBasisChoice] = useState<BasisChoice>('')
  const [basisReason, setBasisReason] = useState('')

  useEffect(() => {
    if (hydrated || !draft) return
    setRates(ratesFrom(draft))
    setOverrideMultiple(str(draft.override_multiple))
    setOverrideRationale(str(draft.override_rationale))
    setOverrideDisposition(str(draft.override_disposition))
    setEarningsOverride(str(draftExtras.earnings_override?.value))
    setEarningsOverrideReason(draftExtras.earnings_override?.reason ?? '')
    setBasisChoice(draftExtras.earnings_basis?.value ?? '')
    setBasisReason(draftExtras.earnings_basis?.reason ?? '')
    setHydrated(true)
  }, [draft, draftExtras, hydrated])

  const [starting, setStarting] = useState<Record<string, HouseDefault> | null>(null)
  useEffect(() => {
    if (!canOverride) return
    let live = true
    fetchValuationMethodology()
      .then(m => { if (live) setStarting(m.input_defaults ?? null) })
      .catch(() => {}) // no starting values: the fields stay as the draft left them
    return () => { live = false }
  }, [canOverride])

  // Once the draft has settled AND the starting values have arrived, fill each
  // rate the draft left empty and the signer has not typed in, through the
  // screen's own state — so the autosave saves it, attributed.
  const touched = useRef(new Set<keyof RatesValues>())
  const startingApplied = useRef(false)
  useEffect(() => {
    if (!hydrated || !starting || !canOverride || startingApplied.current) return
    startingApplied.current = true
    const fill: Partial<RatesValues> = {}
    for (const [draftKey, name] of STARTING_FIELDS) {
      const d = starting[draftKey]
      const saved = draft ? draft[draftKey] : null
      if (d && (saved === null || saved === undefined) && !touched.current.has(name)) fill[name] = String(d.value)
    }
    if (Object.keys(fill).length) setRates(prev => ({ ...prev, ...fill }))
  }, [hydrated, starting, canOverride]) // eslint-disable-line react-hooks/exhaustive-deps

  const on = canOverride && hydrated

  // Rates + the multiple override: what does not parse is omitted and named.
  const rateProblems: string[] = []
  const ratesPatch = ratesAutosavePatch(rates, rateProblems)
  const multiple = overrideAutosavePatch({ overrideMultiple, overrideRationale, overrideDisposition })
  const ratesSave = useAutosave({ ...ratesPatch, ...multiple.patch }, saveDraft, {
    enabled: on, delayMs: AUTOSAVE_MS, blocked: notSaved([...rateProblems, ...multiple.problems]),
  })

  // The earnings override: a number with its reason, or null to clear.
  const overrideText = earningsOverride.trim()
  const overrideValue = overrideText === '' ? null : Number(overrideText)
  const overrideOk = overrideValue === null || (Number.isFinite(overrideValue) && overrideValue > 0)
  const overrideReason = earningsOverrideReason.trim()
  const overrideProblems = !overrideOk ? [EARNINGS_OVERRIDE_LABEL]
    : overrideValue !== null && overrideReason === '' ? [EARNINGS_OVERRIDE_REASON_LABEL] : []
  const earningsSave = useAutosave(
    { tuning: { earnings_override: overrideValue === null ? null : { value: overrideValue, reason: overrideReason } } },
    saveDraft,
    { enabled: on, delayMs: AUTOSAVE_MS, blocked: notSaved(overrideProblems) },
  )

  // The earnings basis: Automatic (null) or a choice with its reason.
  const chosenReason = basisReason.trim()
  const basisSave = useAutosave(
    { tuning: { earnings_basis: basisChoice === '' ? null : { value: basisChoice, reason: chosenReason } } },
    saveDraft,
    { enabled: on, delayMs: AUTOSAVE_MS, blocked: notSaved(basisChoice !== '' && chosenReason === '' ? [BASIS_REASON_LABEL] : []) },
  )

  // The figure normalization gives for the basis in effect, and the one the
  // engine will use (a valid override wins). Without normalization, the draft's
  // figure stands in — unless an override was saved over it.
  const calculated: number | null = normalized
    ? (basisChoice ? normalized.averaged[basisChoice] : null) ?? normalized.normalized_earnings
    : draftExtras.earnings_override ? null : draft?.normalized_earnings ?? null
  const overrideApplies = overrideValue !== null && overrideOk
  const applied = overrideApplies ? overrideValue : calculated
  const basis = basisChoice || normalized?.basis || draft?.basis || null

  /** What the live preview overlays on the saved draft's engine fields. */
  const previewFields = useMemo(
    () => ({ ...ratesPatch, ...multiple.patch }),
    [JSON.stringify(ratesPatch), JSON.stringify(multiple.patch)], // eslint-disable-line react-hooks/exhaustive-deps
  )

  return {
    hydrated,
    rates,
    editRate: (key: keyof RatesValues, v: string) => {
      touched.current.add(key)
      setRates(prev => ({ ...prev, [key]: v }))
    },
    multipleOverride: {
      overrideMultiple, setOverrideMultiple,
      overrideRationale, setOverrideRationale,
      overrideDisposition, setOverrideDisposition,
    },
    ratesMessage: ratesSave.status === 'blocked' || ratesSave.status === 'error' ? ratesSave.message : null,
    earnings: {
      calculated, applied, overrideApplies,
      earningsOverride, setEarningsOverride, earningsOverrideReason, setEarningsOverrideReason,
      overrideMessage: notSaved(overrideProblems) ?? (earningsSave.status === 'error' ? earningsSave.message : null),
      basisChoice, setBasisChoice, basisReason, setBasisReason,
      basisMessage: notSaved(basisChoice !== '' && chosenReason === '' ? [BASIS_REASON_LABEL] : [])
        ?? (basisSave.status === 'error' ? basisSave.message : null),
    },
    basis,
    previewFields,
  }
}
